import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Email as EmailIcon,
  Close as CloseIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';

const ForgotPasswordModal = ({ open, onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const { resetPassword } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email) {
      setError(t('auth.pleaseEnterEmail'));
      setLoading(false);
      return;
    }

    try {
      await resetPassword(email);
      setSuccess(true);
    } catch (error) {
      console.error('Reset password error:', error);
      if (error.code === 'auth/user-not-found') {
        setError(t('auth.noAccountWithEmail'));
      } else if (error.code === 'auth/invalid-email') {
        setError(t('auth.invalidEmail'));
      } else {
        setError(t('auth.errorOccurredTryAgain'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setError('');
    setSuccess(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#2a2a2a',
          borderRadius: 3,
          border: '1px solid rgba(245, 245, 220, 0.2)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
        }
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: '#2a2a2a',
          color: 'white',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 2
        }}
      >
        <Typography
          variant={isMobile ? "h6" : "h5"}
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #f5f5dc 0%, #e8e8c8 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
        >
          {t('auth.forgotPassword')}
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{
            color: 'rgba(255,255,255,0.7)',
            '&:hover': {
              color: '#f5f5dc',
              backgroundColor: 'rgba(245,245,220,0.1)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: '#2a2a2a', py: 3 }}>
        {!success ? (
          <Box>
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                color: 'rgba(255,255,255,0.8)',
                lineHeight: 1.6
              }}
            >
              {t('auth.enterEmailForReset')}
            </Typography>

            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  color: '#ef4444',
                  borderRadius: 2,
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  '& .MuiAlert-icon': {
                    color: '#ef4444'
                  }
                }}
              >
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label={t('auth.emailAddress')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: '#f5f5dc' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    borderRadius: 2,
                    '& fieldset': {
                      borderColor: 'rgba(255,255,255,0.2)',
                      borderWidth: 2
                    },
                    '&:hover fieldset': {
                      borderColor: '#f5f5dc'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#f5f5dc'
                    },
                    '& input': {
                      color: 'white',
                      fontSize: { xs: '1rem', md: '1.1rem' }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255,255,255,0.7)',
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    '&.Mui-focused': {
                      color: '#f5f5dc'
                    }
                  }
                }}
              />
            </Box>
          </Box>
        ) : (
          <Box textAlign="center">
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3
              }}
            >
              <SendIcon sx={{ fontSize: 40, color: 'white' }} />
            </Box>
            
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: '#10b981'
              }}
            >
              {t('auth.emailSent')}
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                mb: 2,
                color: 'rgba(255,255,255,0.8)',
                lineHeight: 1.6
              }}
            >
              {t('auth.resetEmailSentTo')} <strong style={{ color: '#f5f5dc' }}>{email}</strong>
            </Typography>
            
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.6)',
                lineHeight: 1.5
              }}
            >
              {t('auth.checkInboxForInstructions')}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          backgroundColor: '#2a2a2a',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          p: 3,
          gap: 2
        }}
      >
        {!success ? (
          <>
            <Button
              onClick={handleClose}
              sx={{
                color: 'rgba(255,255,255,0.7)',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: 'white'
                }
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              onClick={handleSubmit}
              disabled={loading || !email}
              variant="contained"
              endIcon={<SendIcon />}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #f5f5dc 0%, #e8e8c8 100%)',
                color: '#212121',
                boxShadow: '0 4px 15px rgba(245, 245, 220, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #e8e8c8 0%, #dcdcb8 100%)',
                  boxShadow: '0 6px 20px rgba(245, 245, 220, 0.4)'
                },
                '&:disabled': {
                  background: 'rgba(255,255,255,0.1)',
                  color: 'rgba(255,255,255,0.3)',
                  boxShadow: 'none'
                }
              }}
            >
              {loading ? t('auth.sending') : t('auth.send')}
            </Button>
          </>
        ) : (
          <Button
            onClick={handleClose}
            variant="contained"
            fullWidth
            sx={{
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              background: 'linear-gradient(135deg, #f5f5dc 0%, #e8e8c8 100%)',
              color: '#212121',
              boxShadow: '0 4px 15px rgba(245, 245, 220, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #e8e8c8 0%, #dcdcb8 100%)',
                boxShadow: '0 6px 20px rgba(245, 245, 220, 0.4)'
              }
            }}
          >
            {t('common.close')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ForgotPasswordModal;